import { useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { dataVersionState } from '../state.js';
import { useApi } from '../hooks.js';
import { api } from '../api.js';
import { Card } from '../components/Card.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { Loading } from '../components/Loading.js';
import { ErrorBanner, SubmitButton } from '../components/FormBits.js';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select.js';
import { Table, THeadRow, TH, TD, TR, Ltr } from '../components/ui/table.js';
import type { ClientInfo, PortalUser } from '@agio/core';

/**
 * The two "still owed" flags are independent, and the table shows them
 * separately: resetting a password never asks for a new authenticator, and
 * resetting an authenticator never asks for a new password.
 */
type AdminUser = PortalUser & {
  must_change_password?: boolean;
  must_enroll_totp?: boolean;
  totp_enrolled?: boolean;
};

/** Radix Select has no empty-string item value, so "no client" needs a sentinel. */
const NO_CLIENT = '__none__';

/** One-time reveal of an admin-generated temp password (create/reset). */
function TempPasswordBox({ email, password, onDismiss }: { email: string; password: string; onDismiss: () => void }) {
  return (
    <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm">
      <p className="font-semibold text-amber-900">🔑 סיסמה זמנית עבור <span dir="ltr">{email}</span></p>
      <p className="mt-2 select-all rounded bg-white px-3 py-2 font-mono text-base tracking-wide" dir="ltr">{password}</p>
      <p className="mt-2 text-xs text-amber-800">
        מוצגת פעם אחת בלבד — העבירו למשתמש בערוץ מאובטח. בכניסה הבאה הוא יתבקש לבחור סיסמה חדשה. אפליקציית האימות שלו לא השתנתה.
      </p>
      <button className="mt-2 text-xs underline" onClick={onDismiss}>סגירה</button>
    </div>
  );
}

/** Admin: portal-users table + add/edit/deactivate (port of the Workshop module).
 * ALL credential changes (temp password, OTP reset) happen HERE — never self-serve. */
export function AdminUsersPage() {
  const { data: users, loading } = useApi<AdminUser[]>('/admin/users');
  const { data: clients } = useApi<ClientInfo[]>('/admin/clients');
  const bump = useSetRecoilState(dataVersionState);
  const [form, setForm] = useState({ email: '', displayName: '', clientId: '', role: 'viewer' });
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [temp, setTemp] = useState<{ email: string; password: string } | null>(null);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  // The table showed the raw client_id; the Hebrew name is what admins know.
  const nameOfClient = (id: string) => clients?.find((c) => c.client_id === id)?.display_name_he ?? id;

  // First load only — a row action bumps the data version, and re-fetching must
  // not blank the whole page out to a spinner.
  if (loading && !users) return <Loading />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api.post<{ ok: true; tempPassword?: string }>('/admin/users', { ...form, clientId: form.clientId || null });
      if (res.tempPassword) setTemp({ email: form.email, password: res.tempPassword });
      setForm({ email: '', displayName: '', clientId: '', role: 'viewer' });
      bump((v) => v + 1);
    } catch (err) {
      setError(String((err as Error).message));
    } finally {
      setBusy(false);
    }
  };

  const setActive = async (username: string, isActive: boolean) => {
    await api.patch(`/admin/users/${encodeURIComponent(username)}`, { isActive });
    bump((v) => v + 1);
  };

  const resetPassword = async (username: string) => {
    if (!confirm(`לאפס סיסמה עבור ${username}? הסיסמה הנוכחית תפסיק לעבוד מיד. אפליקציית האימות תישאר כפי שהיא.`)) return;
    setRowBusy(username); setError(null);
    try {
      const res = await api.post<{ tempPassword: string }>(`/admin/users/${encodeURIComponent(username)}/reset-password`, {});
      setTemp({ email: username, password: res.tempPassword });
      bump((v) => v + 1);
    } catch (err) {
      setError(String((err as Error).message));
    } finally {
      setRowBusy(null);
    }
  };

  const resetOtp = async (username: string) => {
    if (!confirm(`לאפס אימות דו-שלבי עבור ${username}? המשתמש יסרוק קוד QR חדש בכניסה הבאה. הסיסמה שלו לא תשתנה.`)) return;
    setRowBusy(username); setError(null);
    try {
      await api.post(`/admin/users/${encodeURIComponent(username)}/reset-otp`, {});
      bump((v) => v + 1);
    } catch (err) {
      setError(String((err as Error).message));
    } finally {
      setRowBusy(null);
    }
  };

  const deleteUser = async (username: string) => {
    setPendingDelete(null);
    setRowBusy(username); setError(null);
    try {
      await api.del(`/admin/users/${encodeURIComponent(username)}`);
      bump((v) => v + 1);
    } catch (err) {
      setError(String((err as Error).message));
    } finally {
      setRowBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-bold">משתמשי פורטל</h1>

      <ConfirmDialog
        open={pendingDelete !== null}
        title="מחיקת משתמש"
        confirmLabel="מחיקה לצמיתות"
        tone="danger"
        confirmPhrase="מאשר"
        onConfirm={() => pendingDelete && void deleteUser(pendingDelete)}
        onCancel={() => setPendingDelete(null)}
      >
        <p>
          למחוק את <span dir="ltr" className="font-mono">{pendingDelete}</span> לצמיתות?
        </p>
        <p className="mt-2">
          הסיסמה, אפליקציית האימות והחשבון עצמו יימחקו. הפעולה אינה הפיכה.
        </p>
        <p className="mt-2 text-muted-foreground">
          כדי לחסום גישה באופן זמני והפיך — השתמשו ב״בטל״ במקום.
        </p>
      </ConfirmDialog>

      <Card title="הוספת משתמש">
        <form className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5" onSubmit={submit}>
          <input dir="ltr" required placeholder="email" className="rounded-md border border-input bg-popover px-3 py-2 text-sm"
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input required placeholder="שם תצוגה" className="rounded-md border border-input bg-popover px-3 py-2 text-sm"
            value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
          <Select dir="rtl" value={form.role} onValueChange={(role) => setForm({ ...form, role })}>
            <SelectTrigger className="w-full bg-popover"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="viewer">viewer</SelectItem>
              <SelectItem value="uber">uber</SelectItem>
            </SelectContent>
          </Select>
          <Select dir="rtl" value={form.clientId || NO_CLIENT} onValueChange={(v) => setForm({ ...form, clientId: v === NO_CLIENT ? '' : v })}>
            <SelectTrigger className="w-full bg-popover"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_CLIENT}>— ללא לקוח (uber) —</SelectItem>
              {(clients ?? []).map((c) => (
                <SelectItem key={c.client_id} value={c.client_id}>{c.display_name_he}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <SubmitButton busy={busy} busyLabel="מוסיף…">הוסף</SubmitButton>
        </form>
        <ErrorBanner error={error} />
        {temp && <TempPasswordBox email={temp.email} password={temp.password} onDismiss={() => setTemp(null)} />}
      </Card>

      <Card title="משתמשים">
        <Table minWidth="38rem">
          <THeadRow>
            <TH>אימייל</TH>
            <TH className="hidden sm:table-cell">שם</TH>
            <TH className="hidden md:table-cell">תפקיד</TH>
            <TH className="hidden lg:table-cell">לקוח</TH>
            <TH className="hidden md:table-cell">סטטוס</TH>
            <TH align="end" />
          </THeadRow>
          <tbody>
            {(users ?? []).map((u) => (
              <TR key={u.username} className="hover:bg-muted/30">
                <TD><Ltr>{u.username}</Ltr></TD>
                <TD className="hidden sm:table-cell">{u.display_name}</TD>
                <TD className="hidden md:table-cell">
                  <span className="rounded-full border border-border px-2 py-0.5 text-xs">{u.role}</span>
                </TD>
                <TD className="hidden text-muted-foreground lg:table-cell">
                  {u.client_id ? nameOfClient(u.client_id) : '—'}
                </TD>
                <TD className="hidden md:table-cell">
                  {u.is_active ? <span className="text-success">פעיל</span> : <span className="text-destructive">מושבת</span>}
                  {u.must_change_password && (
                    <span className="ms-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">ממתין לסיסמה</span>
                  )}
                  {u.must_enroll_totp && (
                    <span className="ms-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">ממתין לאפליקציה</span>
                  )}
                </TD>
                <TD align="end">
                  <span className="flex flex-wrap justify-end gap-1">
                    <button className="whitespace-nowrap rounded-md border border-border px-1.5 py-0.5 text-xs hover:bg-muted disabled:opacity-50"
                      disabled={rowBusy === u.username} onClick={() => resetPassword(u.username)}>
                      סיסמה
                    </button>
                    <button className="whitespace-nowrap rounded-md border border-border px-1.5 py-0.5 text-xs hover:bg-muted disabled:opacity-50"
                      disabled={rowBusy === u.username} onClick={() => resetOtp(u.username)}>
                      OTP
                    </button>
                    <button className="whitespace-nowrap rounded-md border border-border px-1.5 py-0.5 text-xs hover:bg-muted"
                      onClick={() => setActive(u.username, !u.is_active)}>
                      {u.is_active ? 'בטל' : 'הפעל'}
                    </button>
                    <button className="whitespace-nowrap rounded-md border border-destructive/40 px-1.5 py-0.5 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-50"
                      disabled={rowBusy === u.username} onClick={() => setPendingDelete(u.username)}>
                      מחק
                    </button>
                  </span>
                </TD>
              </TR>
            ))}
          </tbody>
        </Table>
      </Card>
    </div>
  );
}
